import {Component, OnInit} from '@angular/core'
import {ScheduleReportService} from '../schedules-reports/schedules-report.service'
import {ReportProblemService} from '../schedules/report-problem-modal/report-problem.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ConfirmationModalComponent } from 'src/app/components/confirmation-modal/confirmation-modal.component'
import { AppConstants } from 'src/app/app.constants'
import { toUTC } from 'src/app/utils/contants'

@Component({
  selector: 'app-schedule-error-report',
  templateUrl: './schedule-error-report.component.html',
  styleUrls: ['./schedule-error-report.component.scss'],
})
export class ScheduleErrorReportComponent implements OnInit {
  startDate: Date = new Date()
  endDate: Date = new Date()
  sortBy = 'eventStartDate'
  excelPassword = ''
  allChannelOptions: ChannelName[] = []
  channelOptions: ChannelName[] = []
  selectedChannels: ChannelName[] = []
  channelsLoading = false
  results: any[] = []
  getParams: PaginationParams = {
    page: 0,
    size: 100,
  }
  isLoading: boolean = false
  totalRecords: number = 0

  readonly columns = [
    'Title',
    'Channel',
    'Event Date - UTC',
    'Event Time - UTC',
    'Error Found',
    'Comment',
    'Created At',
    'Updated At',
    'Updated By',
    'TMS ID',
    'PrgSvcId',
  ]

  constructor(
    private scheduleReportService: ScheduleReportService,
    private reportProblemService: ReportProblemService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadChannels()
  }

  loadChannels() {
    this.channelsLoading = true
    this.scheduleReportService.getReportChannels('').subscribe(
      (response) => {
        this.allChannelOptions = response
          .filter((channel) => channel.channelNames?.length)
          .map((channel) => ({ ...channel.channelNames[0], id: channel.id }))
        this.channelOptions = [...this.allChannelOptions]
        this.channelsLoading = false
      },
      () => {
        this.allChannelOptions = []
        this.channelOptions = []
        this.channelsLoading = false
      }
    )
  }

  filterChannels(search: string) {
    const term = (search || '').trim().toLowerCase()
    this.channelOptions = term
      ? this.allChannelOptions.filter((channel) =>
          channel.value.toLowerCase().includes(term)
        )
      : [...this.allChannelOptions]
  }

  resetChannelSearch() {
    this.filterChannels('')
  }

  isChannelSelected(channel: ChannelName): boolean {
    return this.selectedChannels.some((selected) => selected.id === channel.id)
  }

  compareChannels = (first: ChannelName, second: ChannelName): boolean => {
    return first?.id === second?.id
  }

  isAllCurrentChannelsSelected(): boolean {
    return (
      this.channelOptions.length > 0 &&
      this.channelOptions.every((channel) => this.isChannelSelected(channel))
    )
  }

  toggleSelectAll(event: Event) {
    event.stopPropagation()
    const checked = (event.target as HTMLInputElement).checked

    if (checked) {
      const selectedById = new Map(
        this.selectedChannels.map((channel) => [channel.id, channel])
      )
      this.channelOptions.forEach((channel) =>
        selectedById.set(channel.id, channel)
      )
      this.selectedChannels = Array.from(selectedById.values())
    } else {
      const currentIds = new Set(
        this.channelOptions.map((channel) => channel.id)
      )
      this.selectedChannels = this.selectedChannels.filter(
        (channel) => !currentIds.has(channel.id)
      )
    }
  }

  onScroll(event: any) {
    event.preventDefault()
    const elem = event.target
    const limit = elem.scrollHeight - elem.clientHeight
    if (
      elem.scrollTop > 0 &&
      elem.scrollTop + 1 >= limit &&
      this.isLoading == false
    ) {
      elem.scrollTop -= 10
      this.getParams.page++
      this.search(false)
    }
  }

  search(reset: boolean = true) {
    if (reset) {
      this.reset()
    }

    const body = {
      channelIds: this.selectedChannels.map((channel) => channel.id),
      startDate: toUTC(this.startDate, false),
      endDate: toUTC(this.endDate, true),
      sortBy: this.sortBy,
      page: this.getParams.page,
      size: this.getParams.size,
    }

    this.isLoading = true
    this.reportProblemService.searchReport(body).subscribe((data: any) => {
      this.totalRecords = data?.totalElements || 0
      if (data?.response) {
        if (this.getParams.page < data?.totalPages) {
          if (this.getParams.page === 0) {
            this.results = data.response
          } else {
            const newData = data.response
            this.results.push(...newData)
          }
        } else {
          this.getParams.page--
          if (data?.totalPages === 0) {
            this.results = []
          }
        }
      }
      this.isLoading = false
    })
  }

  export() {

    const body = {
      channelIds: this.selectedChannels.map((channel) => channel.id),
      startDate: toUTC(this.startDate, false),
      endDate: toUTC(this.endDate, true),
      sortBy: this.sortBy,
      password: this.excelPassword,
    }

    const modal = this.modalService.open(ConfirmationModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
    })
    modal.componentInstance.title = 'Submit request'
    modal.componentInstance.message = `Do you want to download the report?`
    modal.componentInstance.isAlert = false
    modal.result.then((result) => {
      if (result) {
        this.isLoading = true
        this.reportProblemService.export(body).subscribe({
          next: (response) => {
            if (response) {
              this.isLoading = false
              this.excelPassword = ''
              const blob = new Blob([response.body], {
                type: response.headers.get('content-type'),
              })
              let fileName = response?.headers
                ?.get('content-disposition')
                ?.split(';')[1]
              fileName = fileName?.replace('"', '')?.split('=')[1]
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)
              link.download = fileName
              link.click()
              this.showAlert(
                AppConstants.SUCCESS,
                `File ${fileName} is downloaded successfully.`
              )
            }
          },
          error: (error) => {
            this.isLoading = false
            this.showAlert(AppConstants.ERROR, `Failed to download the report.`)
            return
          },
        })
      }
    })
  }

  showAlert(title: string, message: string) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
    })
    modalRef.componentInstance.title = title
    modalRef.componentInstance.message = message
    modalRef.componentInstance.isAlert = true
  }

  reset() {
    let container = document.getElementById('tableReport')
    if (container) {
      container.scrollTop = 0
    }
    this.getParams.page = 0
    this.results = []
    this.excelPassword = ''
  }

  isEnableButton() {
    return this.selectedChannels.length > 0 && !this.isLoading
  }
}
